import { resetFilters } from "@app-redux/slices/filtersSlice";
import { resetProfileList } from "@app-redux/slices/profileListSlice";
import { resetUserData } from "@app-redux/slices/userDataSlice";
import { resetRepTop30 } from "@app-redux/slices/repTop30Slice";
import { resetOpportunity } from "@app-redux/slices/opportunitySlice";
import { useAppDispatch } from "@app-redux/store";
import { ROUTER_PATHS } from "@constants/index";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Avatar,
  Badge,
  Fade,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { stringAvatar } from "@utils/index";
import { useConfig } from "@/hooks/queries/useConfig";
import { usePollstarPendingCount } from "@/hooks/queries/usePollstar";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import styles from "./styles.module.css";

export const UserMenu = ({ name, isAdmin }: { name: string; isAdmin?: boolean }) => {
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Surface the Pollstar "pending mappings" badge (shown in App Config) on the always-visible avatar so
  // admins notice outstanding work without opening the menu. Only fetched for admins on enabled instances.
  const { data: config } = useConfig();
  const pollstarEnabled = config?.pollstar_enabled ?? false;
  const { data: pendingCount = 0 } = usePollstarPendingCount(!!isAdmin && pollstarEnabled);
  const showPending = !!isAdmin && pollstarEnabled && pendingCount > 0;

  const handleSignOut = async () => {
    queryClient.clear();
    dispatch(resetUserData());
    dispatch(resetProfileList());
    dispatch(resetFilters());
    dispatch(resetRepTop30());
    dispatch(resetOpportunity());
    await instance.logoutRedirect();
  };

  const openMenu = Boolean(anchorEl);
  return (
    <>
      <IconButton
        sx={{ p: 0 }}
        color="inherit"
        aria-label="Menu"
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          setAnchorEl(event.currentTarget)
        }
        aria-controls={openMenu ? "fade-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? "true" : undefined}
      >
        <Badge
          color="error"
          overlap="circular"
          badgeContent={pendingCount}
          max={99}
          invisible={!showPending}
          aria-label={showPending ? `${pendingCount} Pollstar mappings pending` : undefined}
        >
          <Avatar
            className={styles.userLogo}
            sx={{ width: 32, height: 32 }}
            {...stringAvatar(name)}
          />
        </Badge>
      </IconButton>
      <Menu
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        MenuListProps={{
          "aria-labelledby": "fade-button",
        }}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={() => setAnchorEl(null)}
        TransitionComponent={Fade}
      >
        <MenuItem divider className={styles.pointerNone}>
          <ListItemText className="list-header">{name}</ListItemText>
        </MenuItem>
        {isAdmin && (
          <li>
            <NavLink to={ROUTER_PATHS.appConfig}>
              {({ isActive }) => (
                <MenuItem
                  className={styles.linkMenuItem}
                  divider
                  component="div"
                  selected={isActive}
                  onClick={() => {
                    setAnchorEl(null);
                  }}
                >
                  <ListItemIcon>
                    <Badge color="error" badgeContent={pendingCount} max={99} invisible={!showPending}>
                      <SettingsIcon color="inherit" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText>App Configuration</ListItemText>
                </MenuItem>
              )}
            </NavLink>
          </li>
        )}
        <MenuItem
          divider
          onClick={() => {
            handleSignOut();
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <LogoutIcon color="inherit" />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
